import { Alert, Descriptions, Steps } from 'antd';
import { ArrowLeft, Copy, ExternalLink, RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  TASK_ID_PATTERN,
  taskPlatformApi,
  taskPlatformErrorMessage,
} from '@/services/taskPlatformApi';
import type { TaskExecution } from '@/types/taskPlatform';
import {
  acceptanceTag,
  deliveryTag,
  executionStatusTag,
  formatTaskDuration,
  formatTaskTime,
  outcomeTag,
} from './status';

function stepStatus(
  task: TaskExecution,
  stage: 'acceptance' | 'execution' | 'delivery',
) {
  if (stage === 'acceptance') {
    return task.acceptance === 'confirmed' ? 'finish' : 'process';
  }
  if (stage === 'execution') {
    if (task.failedStage === 'execution' || task.failedStage === 'workflow') {
      return 'error';
    }
    if (task.mainOutcome === 'succeeded') return 'finish';
    return task.executionStatus === 'RUNNING' ? 'process' : 'wait';
  }
  if (task.deliveryStatus === 'failed') return 'error';
  if (task.deliveryStatus === 'succeeded') return 'finish';
  return task.mainOutcome === 'succeeded' ? 'process' : 'wait';
}

export function TaskExecutionDetailPage() {
  const navigate = useNavigate();
  const { taskId = '' } = useParams();
  const [task, setTask] = useState<TaskExecution | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!TASK_ID_PATTERN.test(taskId)) {
      setError('Task ID 无效');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      setTask(await taskPlatformApi.getTask(taskId));
    } catch (loadError) {
      setError(
        taskPlatformErrorMessage(loadError, '任务不存在或当前账号无权查看'),
      );
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => navigate('/tasks')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          返回任务
        </Button>
        <Alert type="error" showIcon message={error} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <Button
            variant="ghost"
            onClick={() =>
              navigate(
                task?.workerPoolId
                  ? `/tasks/workers/${task.workerPoolId}`
                  : '/tasks',
              )
            }
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            返回所属 Worker
          </Button>
          <h1 className="mt-3 max-w-4xl break-all font-mono text-xl font-semibold text-text-strong">
            {taskId}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">任务执行详情</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => void navigator.clipboard.writeText(taskId)}
          >
            <Copy className="mr-1 h-3.5 w-3.5" />
            复制 Task ID
          </Button>
          <Button variant="outline" size="sm" onClick={() => void load()}>
            <RefreshCw className="mr-1 h-3.5 w-3.5" />
            刷新状态
          </Button>
        </div>
      </div>

      {task && !task.sourceAvailable ? (
        <Alert
          type="warning"
          showIcon
          message="当前无法取得 AWS 执行状态"
          description="unknown 不代表业务失败；已持久确认的接收状态仍然保留。"
        />
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>生命周期</CardTitle>
          <CardDescription>
            接收、主执行和结果投递是三条独立状态，不合并成单一成功或失败。
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Steps
            responsive
            items={
              task
                ? [
                    {
                      title: '平台接收',
                      description: acceptanceTag(task.acceptance),
                      status: stepStatus(task, 'acceptance'),
                    },
                    {
                      title: '主执行',
                      description: (
                        <div className="space-x-1">
                          {executionStatusTag(task.executionStatus)}
                          {outcomeTag(task.mainOutcome)}
                        </div>
                      ),
                      status: stepStatus(task, 'execution'),
                    },
                    {
                      title: '结果投递',
                      description: deliveryTag(task.deliveryStatus),
                      status: stepStatus(task, 'delivery'),
                    },
                  ]
                : []
            }
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex-row items-start justify-between space-y-0">
          <div>
            <CardTitle>任务信息</CardTitle>
            <CardDescription>
              执行时间来自 AWS Step Functions，状态为最终一致性观察。
            </CardDescription>
          </div>
          {task?.workerPoolId ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/tasks/workers/${task.workerPoolId}`)}
            >
              所属 Worker
              <ExternalLink className="ml-1 h-3.5 w-3.5" />
            </Button>
          ) : null}
        </CardHeader>
        <CardContent>
          <Descriptions bordered column={{ xs: 1, md: 2, xl: 3 }}>
            <Descriptions.Item label="Task Type">
              <span className="font-mono">{task?.taskType || '—'}</span>
            </Descriptions.Item>
            <Descriptions.Item label="Producer">
              {task?.producer || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Policy Version">
              {task?.policyVersion || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Lane">
              <span className="font-mono">{task?.executionLane || '—'}</span>
            </Descriptions.Item>
            <Descriptions.Item label="Generation">
              <span className="font-mono">{task?.laneGeneration || '—'}</span>
            </Descriptions.Item>
            <Descriptions.Item label="Failed Stage">
              {task?.failedStage || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="创建时间">
              {formatTaskTime(task?.createdAt)}
            </Descriptions.Item>
            <Descriptions.Item label="开始时间">
              {formatTaskTime(task?.startedAt)}
            </Descriptions.Item>
            <Descriptions.Item label="完成时间">
              {formatTaskTime(task?.completedAt)}
            </Descriptions.Item>
            <Descriptions.Item label="耗时">
              {formatTaskDuration(
                task?.startedAt,
                task?.completedAt,
                task?.observedAt,
              )}
            </Descriptions.Item>
            <Descriptions.Item label="状态获取时间">
              {formatTaskTime(task?.observedAt)}
            </Descriptions.Item>
            <Descriptions.Item label="状态来源">
              {task?.sourceAvailable ? 'AWS 可用' : 'AWS 暂不可用'}
            </Descriptions.Item>
          </Descriptions>
        </CardContent>
      </Card>

      {loading ? (
        <div className="text-sm text-muted-foreground">正在加载…</div>
      ) : null}
    </div>
  );
}
